import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { Form, Formik } from "formik";
import { PMField } from "../../../components/utils/PMField";
import toast from "react-hot-toast";
import { dispatchWithToast } from "../../../components/Utils";
import { updateUserAsync } from "../../../slices/user";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import PMListbox from "../../../components/utils/PMListbox";
import PMSwitch from "../../../components/utils/PMSwitch";
import { CheckBadgeIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { fetchUser } from "../../../api/users";
import { useEffect, useState } from "react";
import { useSettings } from "../../../contexts/settings-context";
import { setCurrentUser } from "../../../slices/session";
import { Link } from "react-router-dom";
import { MicrosoftConnectPanel } from "../../../components/dashboard/microsoft/MicrosoftConnectPanel";
import {
  disconnectUserMicrosoft,
  fetchUserMicrosoftStatus,
} from "../../../api/microsoft";
import { useTranslation } from "react-i18next";

export const UserSettings = (props) => {
  const currentUser = useSelector((state) => state.session.currentUser);
  const { settings, saveSettings } = useSettings();


  const [userSettings, setUserSettings] = useState({
    preferred_maps_service: currentUser?.preferred_maps_service,
    language: settings.language,
    time_registration_format: currentUser?.time_registration_format || "hours_minutes",
    use_legacy_variables: currentUser?.use_legacy_variables || false,
  });

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fetchMicrosoftStatus = async () => {
    try {
      return await fetchUserMicrosoftStatus();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404 || status === 501) {
        return { connected: false, apiMissing: true };
      }
      return { connected: false };
    }
  };

  const getUser = async () => {
    await fetchUser(currentUser.id)
      .then((response) => {
        setUserSettings({
          preferred_maps_service: response.data.preferred_maps_service,
          language: response.data.locale,
          time_registration_format: response.data.time_registration_format || "hours_minutes",
          use_legacy_variables: response.data.use_legacy_variables || false,
        });
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
      });
  };

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    setUserSettings((prev) => ({ ...prev, language: settings.language }));
  }, [settings.language]);

  const mapsServices = [
    {
      name: "preferred_maps_service",
      value: "GOOGLE_MAPS",
      label: "Google Maps",
      icon: CheckBadgeIcon,
    },
    {
      name: "preferred_maps_service",
      value: "APPLE_MAPS",
      label: "Apple Maps",
      icon: MapPinIcon,
    },
  ];

  const languages = [
    { name: "language", value: "no", label: "Norsk" },
    { name: "language", value: "en", label: "English" },
    { name: "language", value: "se", label: "Svenska" },
  ];

  const timeRegistrationFormats = [
    { name: "time_registration_format", value: "hours_minutes", label: "Timer og minutter" },
    { name: "time_registration_format", value: "decimal", label: "Kun tid (desimal)" },
  ];

  const handleListboxChange = (e) => {
    const value = e.value !== undefined ? e.value : e.checked;
    const data = {
      id: currentUser.id,
      preferred_maps_service:
        e.name === "preferred_maps_service"
          ? value
          : userSettings.preferred_maps_service,
      locale: e.name === "language" ? value : userSettings.language,
      time_registration_format:
        e.name === "time_registration_format"
          ? value
          : userSettings.time_registration_format,
      use_legacy_variables:
        e.name === "use_legacy_variables"
          ? value
          : userSettings.use_legacy_variables,
    };
    setUserSettings((prev) => ({ ...prev, [e.name]: value }));
    
    // Use the promise directly from dispatch instead of dispatchWithToast
    const updatePromise = dispatch(updateUserAsync(data));
    
    // Handle the promise directly
    updatePromise.then((result) => {
      if (result?.payload && !result.error) {
        // Update Redux session store with updated user data
        dispatch(setCurrentUser({
          ...currentUser,
          preferred_maps_service: data.preferred_maps_service,
          locale: data.locale,
          time_registration_format: data.time_registration_format,
          use_legacy_variables: data.use_legacy_variables,
        }));
        
        // Show success toast
        toast.success("Innstillinger oppdatert");
      } else {
        toast.error("Kunne ikke oppdatere innstillinger");
      }
    }).catch((error) => {
      console.error("Error updating user settings:", error);
      toast.error("Kunne ikke oppdatere innstillinger");
    });
    
    if (e.name === "language") {
      saveSettings({ ...settings, language: e.value });
    }
  };

  function UserForm() {
    return (
      <div>
        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6 sm:col-span-6">
            <PMListbox
              name="preferred_maps_service"
              label={"Ønsket karttjeneste"}
              options={mapsServices}
              selected={userSettings?.preferred_maps_service}
              onChange={handleListboxChange}
            />
          </div>
          <div className="col-span-6 sm:col-span-6">
            <PMListbox
              name="language"
              label={"Språk"}
              options={languages}
              selected={userSettings?.language}
              onChange={handleListboxChange}
            />
          </div>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Timeregistrering
          </h3>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-6">
              <PMListbox
                name="time_registration_format"
                label={"Format for timeregistrering"}
                options={timeRegistrationFormats}
                selected={userSettings?.time_registration_format}
                onChange={handleListboxChange}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Variabler
          </h3>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-6">
              <PMSwitch
                name="use_legacy_variables"
                label={"Bruk legacy variabel system"}
                checked={userSettings?.use_legacy_variables || false}
                onChange={(e) => {
                  handleListboxChange({
                    name: "use_legacy_variables",
                    value: e.target.checked,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {t("microsoft.user.settings_section_title")}
            </h3>
            <Link
              to="/dashboard/user/microsoft"
              className="text-sm font-medium text-sky-600 hover:text-sky-500"
            >
              {t("microsoft.user.open_full_page")} →
            </Link>
          </div>
          <p className="mb-4 text-sm text-gray-600">
            {t("microsoft.user.settings_section_intro")}
          </p>
          <MicrosoftConnectPanel
            variant="user"
            titleKey="microsoft.user.card_title"
            intent="user_mail"
            fetchStatus={fetchMicrosoftStatus}
            onDisconnect={disconnectUserMicrosoft}
          />
        </div>

        {/* <FormikSaveBar {...formik} /> */}
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle={currentUser.name}
        actions={[]}
        title={"Innstillinger"}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3 bg-white shadow rounded-xl px-5 py-5">
            <UserForm />
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};
